import * as React from 'react';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Maybe } from 'graphql/jsutils/Maybe';
import { keyBy } from 'lodash';
import { useIntl } from 'react-intl';
import { Navigate, useLocation, useMatch, useNavigate } from 'react-router';
import styled from 'styled-components';

import { EmptyState } from 'components/features/components/empty-state';
import { Picture } from 'components/picture';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useLoyaltyFeaturedRewardsQuery } from 'generated/sanity-graphql';
import { useEffectOnceOnCondition } from 'hooks/use-effect-once-on-condition';
import { useFeatureLoyaltyRewardListCategories } from 'hooks/use-feature-loyalty-reward-list-categories';
import { useFeatureLoyaltyTiersRewardList } from 'hooks/use-feature-loyalty-tiers-reward-list';
import { useFeatureLoyaltyUi } from 'hooks/use-feature-loyalty-ui';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EVENT_PAGE_NAMES, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useRewardsEvaluation } from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import { RBIBrand, brand } from 'utils/environment';
import { EventName, emitEvent } from 'utils/event-hub';
import {
  PerformancePages,
  initializePagePerformance,
  pageLoaded,
  pageUnloaded,
  performanceReport,
} from 'utils/render-performance';

import { LoyaltyIncentiveNotAvailableModal } from '../loyalty-incentives-components/loyalty-incentive-not-available-modal';
import { useFeaturesLoyaltyNoReward } from '../loyalty-no-reward-points/use-features-loyalty-no-reward';
import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { SHOULD_EXPAND_CATEGORIES } from './constants';
import { InlineRewardsSkeleton } from './loyalty-inline-rewards-skeleton';
import { EmptyStateContainer, StyledLoyaltyLoader } from './loyalty-rewards.styled';
import { LoyaltyRewardsView } from './loyalty-rewards.view';
import { NonNullableReward, NonNullableRewardCategory } from './types';
/**
 * Determines the category and reward that should be open by default based on the passed pre-selections
 * @param rewardCategories filtered categories
 * @param preselectedCategoryLabel pre-selected category label taken from the current route
 * @param preselectedRewardId pre-selected reward id taken from the current route
 */
export const getRewardCategoriesDefaultSelection = (
  rewardCategories: NonNullableRewardCategory[],
  preselectedCategoryLabel?: string | null,
  preselectedRewardId?: string
): [Maybe<NonNullableRewardCategory>, Maybe<NonNullableReward>] => {
  let selectedCategory;
  let selectedReward;
  let selectedRewardParentCategory;
  for (const category of rewardCategories) {
    if (preselectedCategoryLabel === category.label?.locale) {
      selectedCategory = category;
    }
    for (const reward of category.rewards ?? []) {
      if (preselectedRewardId === reward?._id) {
        selectedReward = reward;
        selectedRewardParentCategory = category;
      }
    }
  }

  const defaultCategory = selectedReward
    ? selectedRewardParentCategory
    : selectedCategory || rewardCategories?.find(Boolean);
  const defaultReward = selectedReward || defaultCategory?.rewards?.find(Boolean);
  return [defaultCategory, defaultReward];
};

const useFilteredRewardCategories = (userTier: LoyaltyTierKey | undefined) => {
  const { data: baseCategories, loading: baseCategoriesLoading } =
    useFeatureLoyaltyRewardListCategories();
  const { data: tierRewardsList, loading: tierRewardsListLoading } =
    useFeatureLoyaltyTiersRewardList(userTier);

  const {
    sanityRewardsMap,
    engineRewardsMap,
    loading: loyaltyRewardsLoading,
  } = useLoyaltyRewardsList();

  const rewardCategories = useMemo(
    () =>
      (userTier && tierRewardsList?.rewardCategories
        ? tierRewardsList?.rewardCategories
        : baseCategories
      )?.reduce((filteredCategories: NonNullableRewardCategory[], category) => {
        if (category?.rewards?.length && sanityRewardsMap) {
          const filteredRewards = category.rewards.filter(
            reward =>
              reward?._id &&
              sanityRewardsMap[reward._id] &&
              engineRewardsMap[reward?.loyaltyEngineId || '']
          );
          if (filteredRewards.length) {
            filteredCategories.push({ ...category, rewards: filteredRewards || [] });
          }
        }
        return filteredCategories;
      }, []),
    [
      baseCategories,
      engineRewardsMap,
      sanityRewardsMap,
      userTier,
      tierRewardsList?.rewardCategories,
    ]
  );

  return {
    data: rewardCategories ?? [],
    loading: baseCategoriesLoading || loyaltyRewardsLoading || tierRewardsListLoading,
  };
};

interface ILoyaltyRewardsProps {
  title?: string;
  isWidget?: boolean;
  isInline?: boolean;
}

const LoyaltyRewardsLogoPLK = styled.div`
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

/**
 *
 * LoyaltyRewards displays a list of available rewards that can be redeemed at checkout
 *
 */
export const LoyaltyRewards: FC<ILoyaltyRewardsProps> = ({
  title,
  isWidget = false,
  isInline = false,
}) => {
  // Track page loading performance
  if (!isInline) {
    initializePagePerformance(PerformancePages.REWARDS_PAGE);
  }

  const { user } = useAuthContext();
  const [selectedRewardIdFromWidget, setSelectedRewardIdFromWidget] = useState('');
  const { data: noRewardsData, loading: noRewardsLoading } = useFeaturesLoyaltyNoReward();
  const { trackEvent } = useCdpContext();
  const loyaltyEnabled = useIsLoyaltyEnabled();

  const { loyaltyUser } = useLoyaltyUser();
  const { enableLoyaltyTiers } = useLoyaltyRewards(loyaltyUser);
  const enableLoyaltyTiersFlag = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_TIERS);
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();

  const { rewards, engineRewardsMap, loading, sanityRewardsMap, refetchRewards } =
    useLoyaltyRewardsList();

  const { rewardsEvaluationMap } = useRewardsEvaluation();
  const { featureLoyaltyUi, featureLoyaltyUiLoading } = useFeatureLoyaltyUi();

  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const isDesktop = useMediaQuery(MediaQuery.Desktop);
  const isWidgetOrDesktop = isWidget || isDesktop;

  const { search, pathname } = useLocation();
  const { setEventPageName } = useLocationContext();

  const { params } =
    useMatch({ path: `${formatMessage({ id: 'routes.loyaltyHome' })}/:tab/*` }) || {};

  // If using as widget, the reward ID is saved in state
  const selectedRewardId =
    isWidgetOrDesktop || isInline ? selectedRewardIdFromWidget : params?.['*'];
  const rewardsTabPath = params?.tab!;
  const queryString = new URLSearchParams(search);
  const preselectedCategoryLabel = queryString.get('category');

  useEffectOnceOnCondition(
    () => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyRewards',
      });
    },
    () => !selectedRewardId && !isInline
  );

  // Local state for opened accordions
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { data: rewardCategories, loading: rewardCategoriesLoading } = useFilteredRewardCategories(
    enableLoyaltyTiers && enableLoyaltyTiersFlag ? userTier : LoyaltyTierKey.TIER_1
  );
  const rewardCategoriesMap = keyBy(rewardCategories, cat => cat?.label?.locale);
  const [defaultCategory, defaultReward] = getRewardCategoriesDefaultSelection(
    rewardCategories,
    preselectedCategoryLabel,
    selectedRewardId
  );

  // Prefetch featured rewards without blocking rendering main rewards
  useLoyaltyFeaturedRewardsQuery();

  const rewardsDetailsRoute =
    featureLoyaltyUi?.loyaltyCTAButtons?.loyaltyBannerHomepageAuthenticatedButton
      ?.loyaltyBannerHomepageAuthenticatedLink?.locale ||
    formatMessage({ id: 'routes.loyaltyDashboard' });

  const handleRewardClick = useCallback(
    ({
      incentiveName,
      loyaltyEngineId,
      incentiveSanityId,
      rewardLocked,
      rewardCategory,
    }: {
      incentiveName: string;
      loyaltyEngineId: string;
      incentiveSanityId: string;
      rewardLocked: boolean;
      rewardCategory: string;
    }) =>
      () => {
        if (selectedRewardId !== incentiveSanityId) {
          trackEvent({
            name: CustomEventNames.CARD,
            type: EventTypes.Other,
            attributes: {
              Name: 'Reward',
              Path: pathname,
              Type: incentiveSanityId,
              Status: rewardLocked ? 'Locked' : 'Unlocked',
              Category: rewardCategory,
            },
          });
          trackEvent({
            name: CustomEventNames.REWARD_SELECTED,
            type: EventTypes.Other,
            attributes: {
              engineId: loyaltyEngineId,
              sanityId: incentiveSanityId,
              name: incentiveName,
              Status: rewardLocked ? 'Locked' : 'Unlocked',
              Category: rewardCategory,
            },
          });
          if (isWidgetOrDesktop) {
            setSelectedRewardIdFromWidget(incentiveSanityId);
            return;
          }

          setEventPageName(EVENT_PAGE_NAMES.RewardDetail);
          if (!isInline) {
            navigate(incentiveSanityId);
          } else {
            navigate(`${rewardsDetailsRoute}/${incentiveSanityId}?inline=true`);
          }
        }
      },
    [isWidgetOrDesktop, trackEvent, navigate, selectedRewardId, rewardsDetailsRoute]
  );

  // Set default opened accordion based on pre-selections
  useEffect(() => {
    const isSelectedCategoryAvailable = selectedCategories.some(cat => rewardCategoriesMap[cat]);
    if ((!selectedCategories.length || !isSelectedCategoryAvailable) && SHOULD_EXPAND_CATEGORIES) {
      const categoryLabel = defaultCategory?.label?.locale;
      if (categoryLabel) {
        setSelectedCategories([categoryLabel]);
      }
    }
    // selectedCategories should not be in the deps, to prevent setting defaultCategory when all accordions are manually closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCategory]);

  // If reward ID is present in URL try to open it directly
  useEffect(() => {
    if (isInline) {
      return;
    }

    const rewardIdFromUrl = params?.['*'];

    if (rewardIdFromUrl?.length) {
      setSelectedRewardIdFromWidget(rewardIdFromUrl);
    }
  }, [params, isInline]);

  useEffect(() => {
    if (isInline) {
      return;
    }

    initializePagePerformance(PerformancePages.REWARDS_PAGE);

    // If offers are still loading do nothing
    if (loading || rewardCategoriesLoading || featureLoyaltyUiLoading) {
      return;
    }

    requestAnimationFrame(() => {
      // Page content loaded event
      pageLoaded(PerformancePages.REWARDS_PAGE);

      if (loading || rewardCategoriesLoading || featureLoyaltyUiLoading) {
        return;
      }

      trackEvent({
        name: CustomEventNames.LOYALTY_LOAD,
        type: EventTypes.Other,
        attributes: performanceReport(PerformancePages.REWARDS_PAGE),
      });
    });

    return () => {
      pageUnloaded(PerformancePages.REWARDS_PAGE);
    };
  }, [loading, rewardCategoriesLoading, featureLoyaltyUiLoading, isInline]);

  if (loading || noRewardsLoading || featureLoyaltyUiLoading || rewardCategoriesLoading) {
    if (brand() === RBIBrand.PLK && loyaltyEnabled) {
      return (
        <LoyaltyRewardsLogoPLK>
          <Picture
            image={featureLoyaltyUi?.rewardsLogo?.locale}
            alt={formatMessage({ id: 'rewards' })}
            objectFitContain={true}
          />
        </LoyaltyRewardsLogoPLK>
      );
    } else if (isInline) {
      return <InlineRewardsSkeleton />;
    } else {
      return <StyledLoyaltyLoader />;
    }
  }

  if (!user?.loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  const selectedReward = sanityRewardsMap?.[selectedRewardId || ''];
  if (sanityRewardsMap && selectedRewardId && !selectedReward) {
    return <LoyaltyIncentiveNotAvailableModal />;
  }

  // Do not redirect if it's used as a widget
  // Redirect to first reward on current category if there isn't one pre-selected
  if (!isWidgetOrDesktop && isDesktop && !selectedRewardId && rewards?.length && defaultReward) {
    return <Navigate to={defaultReward._id} replace />;
  }

  if (!rewardCategories?.length && noRewardsData && noRewardsData.noRewardsAvailable) {
    if (isInline) {
      return <InlineRewardsSkeleton />;
    }

    return (
      <EmptyStateContainer>
        <EmptyState content={noRewardsData.noRewardsAvailable} showCTA={false} />
      </EmptyStateContainer>
    );
  }

  return rewardCategories?.length && sanityRewardsMap ? (
    <LoyaltyRewardsView
      selectedReward={selectedReward}
      handleClick={handleRewardClick}
      sanityRewardsMap={sanityRewardsMap}
      rewardCategories={rewardCategories}
      engineRewardsMap={engineRewardsMap}
      formatMessage={formatMessage}
      rewardsTabPath={rewardsTabPath}
      selectedCategories={selectedCategories}
      setSelectedCategories={setSelectedCategories}
      refetchRewards={refetchRewards}
      rewardsEvaluationMap={rewardsEvaluationMap}
      isWidget={isWidgetOrDesktop}
      isCarousel={true}
      isInline={isInline}
      title={title}
      currentUserTier={enableLoyaltyTiers ? userTier : undefined}
      setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
    />
  ) : null;
};

export const LoyaltyRewardsMemo = memo(LoyaltyRewards);
